import { getDateTimeStringWithTimezone, handleApiError } from '../../common/util';
import { ILabourClockingDetails } from '../../models/dms/clocking.model';
import { IRepairOrderJob } from '../../models/dms/repairOrderJob.model';
import { IRepairOrder } from '../../models/dms/repairOrder.model';
import { IAddSync, SyncDataType, SyncType } from '../../models/dms/sync.model';
import { IGeoposition } from '../../models/geolocation.model';
import axiosDmsInstance from './axiosDmsInstance';
import { ITechCommentPost } from '../../models/dms/technicianComments.model';
import { IAttachment } from '../../models/dms/attachment.model';

export const clockOnAttendance = async (batchId: string, techCode: string, startDateTime: Date, position?: IGeoposition) => {
  try {
    const startDateTimeString = getDateTimeStringWithTimezone(startDateTime);
    const url = `/api/common/sync/v1/addSync`;
    let params = {} as any;

    const body = {
      batchId,
      name: 'TMC',
      type: SyncType.TechAttendanceOn,
      syncData: [
        {
          field: 'TechCode',
          dataType: SyncDataType.Text,
          value: techCode,
        },
        {
          field: 'StartDateTime',
          dataType: SyncDataType.Date,
          value: startDateTimeString,
        },
        {
          field: 'Latitude',
          dataType: SyncDataType.Text,
          value: position?.latitude?.toString(),
        },
        {
          field: 'Longitude',
          dataType: SyncDataType.Text,
          value: position?.longitude?.toString(),
        },
        {
          field: 'Accuracy',
          dataType: SyncDataType.Text,
          value: position?.accuracy?.toString(),
        },
      ],
    } as IAddSync;

    const response = await axiosDmsInstance.post(`${url}`, body, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('clockOnAttendance', err);
  }
};

export const clockOffAttendance = async (batchId: string, techCode: string, endDateTime: Date, position?: IGeoposition) => {
  try {
    const endDateTimeString = getDateTimeStringWithTimezone(endDateTime);
    const url = `/api/common/sync/v1/addSync`;
    let params = {} as any;

    const body = {
      batchId,
      name: 'TMC',
      type: SyncType.TechAttendanceOff,
      syncData: [
        {
          field: 'TechCode',
          dataType: SyncDataType.Text,
          value: techCode,
        },
        {
          field: 'EndDateTime',
          dataType: SyncDataType.Date,
          value: endDateTimeString,
        },
        {
          field: 'Latitude',
          dataType: SyncDataType.Text,
          value: position?.latitude?.toString(),
        },
        {
          field: 'Longitude',
          dataType: SyncDataType.Text,
          value: position?.longitude?.toString(),
        },
        {
          field: 'Accuracy',
          dataType: SyncDataType.Text,
          value: position?.accuracy?.toString(),
        },
      ],
    } as IAddSync;

    const response = await axiosDmsInstance.post(`${url}`, body, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('clockOffAttendance', err);
  }
};

export const clockOnLabour = async (batchId: string, techCode: string, startDateTime: Date, jobData: IRepairOrderJob, repairOrder: IRepairOrder, position?: IGeoposition) => {
  try {
    const startDateTimeString = getDateTimeStringWithTimezone(startDateTime);
    const url = `/api/common/sync/v1/addSync`;
    let params = {} as any;

    let body = {
      batchId,
      name: 'TMC',
      type: SyncType.TechLabourOn,
      syncData: [
        {
          field: 'TechCode',
          dataType: SyncDataType.Text,
          value: techCode,
        },
        {
          field: 'StartDateTime',
          dataType: SyncDataType.Date,
          value: startDateTimeString,
        },
      ],
    } as IAddSync;

    if (jobData.isAdhoc) {
      // Adhoc clocking
      body.type = SyncType.TechLabourOnAdhoc;
      body.syncData = [
        ...body.syncData,
        {
          field: 'AdhocCustomerName',
          dataType: SyncDataType.Text,
          value: repairOrder.driver.name,
        },
        {
          field: 'AdhocRepairOrderNumber',
          dataType: SyncDataType.Text,
          value: repairOrder.repairOrderNumber?.toString(),
        },
        {
          field: 'AdhocRegistrationNumber',
          dataType: SyncDataType.Text,
          value: repairOrder.registrationNumber,
        },
        {
          field: 'AdhocVehicleDetails',
          dataType: SyncDataType.Text,
          value: repairOrder.vehicle?.description,
        },
        {
          field: 'AdhocVin',
          dataType: SyncDataType.Text,
          value: repairOrder.vehicle.vin || '',
        },
      ];
    } else {
      // Normal clocking
      body.syncData = [
        ...body.syncData,
        {
          field: 'TechCode',
          dataType: SyncDataType.Text,
          value: techCode,
        },
        {
          field: 'BranchCode',
          dataType: SyncDataType.Text,
          value: jobData.branchCode,
        },
        {
          field: 'RepairOrderNumber',
          dataType: SyncDataType.Number,
          value: jobData.repairOrderNumber.toString(),
        },
        {
          field: 'JobCode',
          dataType: SyncDataType.Text,
          value: jobData.jobCode,
        },
        {
          field: 'JobType',
          dataType: SyncDataType.Text,
          value: jobData.jobType,
        },
      ];
    }

    // Location
    if (position?.latitude && position?.longitude) {
      body.syncData = [
        ...body.syncData,
        {
          field: 'Latitude',
          dataType: SyncDataType.Text,
          value: position.latitude.toString(),
        },
        {
          field: 'Longitude',
          dataType: SyncDataType.Text,
          value: position?.longitude?.toString(),
        },
        {
          field: 'Accuracy',
          dataType: SyncDataType.Text,
          value: position?.accuracy?.toString() || '0',
        },
      ];
    }

    const response = await axiosDmsInstance.post(`${url}`, body, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('clockOnLabour', err);
  }
};

export const clockOffLabour = async (batchId: string, techCode: string, endDateTime: Date, jobData: IRepairOrderJob, repairOrder: IRepairOrder, details?: ILabourClockingDetails, position?: IGeoposition) => {
  try {
    const endDateTimeString = getDateTimeStringWithTimezone(endDateTime);
    const url = `/api/common/sync/v1/addSync`;
    let params = {} as any;

    let body = {
      batchId,
      name: 'TMC',
      type: SyncType.TechLabourOff,
      syncData: [
        {
          field: 'TechCode',
          dataType: SyncDataType.Text,
          value: techCode,
        },
        {
          field: 'EndDateTime',
          dataType: SyncDataType.Date,
          value: endDateTimeString,
        },
      ],
    } as IAddSync;

    if (jobData.isAdhoc) {
      // Adhoc clocking
      body.type = SyncType.TechLabourOffAdhoc;
      body.syncData = [
        ...body.syncData,
        {
          field: 'AdhocCustomerName',
          dataType: SyncDataType.Text,
          value: repairOrder.driver.name,
        },
        {
          field: 'AdhocRepairOrderNumber',
          dataType: SyncDataType.Text,
          value: repairOrder.repairOrderNumber?.toString(),
        },
        {
          field: 'AdhocRegistrationNumber',
          dataType: SyncDataType.Text,
          value: repairOrder.registrationNumber,
        },
        {
          field: 'AdhocVehicleDetails',
          dataType: SyncDataType.Text,
          value: repairOrder.vehicle?.description,
        },
        {
          field: 'AdhocVin',
          dataType: SyncDataType.Text,
          value: repairOrder.vehicle.vin || '',
        },
      ];

      if (details && details.techComments.length) {
        // Convert tech comments to POST payload
        const techCommentsPost = details.techComments.map((techComment) => {
          return {
            repairOrderNumber: techComment.repairOrderNumber,
            branch: techComment.branch,
            jobType: techComment.jobType,
            jobCode: techComment.jobCode,
            comment: techComment.comment,
            technicianCode: techComment.technicianCode,
            actioned: techComment.actioned,
            isAlert: techComment.isAlert,
          } as ITechCommentPost;
        });
        body.syncData = [
          ...body.syncData,
          {
            field: 'AdhocComments',
            dataType: SyncDataType.Text,
            value: JSON.stringify(techCommentsPost),
          },
        ];
      }

      if (details && details.attachments.length) {
        // Convert attachments to POST payload
        const attachmentsPost = details.attachments.map((attachment) => {
          return attachment.base64Url
            ? ({
                base64Url: attachment.base64Url,
              } as IAttachment)
            : ({
                ...attachment,
              } as IAttachment);
        });
        body.syncData = [
          ...body.syncData,
          {
            field: 'AdhocAttachments',
            dataType: SyncDataType.Text,
            value: JSON.stringify(attachmentsPost),
          },
        ];
      }
    } else {
      // Normal clocking
      body.syncData = [
        ...body.syncData,
        {
          field: 'BranchCode',
          dataType: SyncDataType.Text,
          value: jobData.branchCode,
        },
        {
          field: 'RepairOrderNumber',
          dataType: SyncDataType.Number,
          value: jobData.repairOrderNumber.toString(),
        },
        {
          field: 'JobCode',
          dataType: SyncDataType.Text,
          value: jobData.jobCode,
        },
        {
          field: 'JobType',
          dataType: SyncDataType.Text,
          value: jobData.jobType,
        },
      ];
    }

    // Details
    if (details) {
      body.syncData = [
        ...body.syncData,
        {
          field: 'IsAlert',
          dataType: SyncDataType.Text,
          value: details.isAlert ? 'Y' : 'N',
        },
        {
          field: 'TechComment',
          dataType: SyncDataType.Text,
          value: details.techComment,
        },
        {
          field: 'DelayCode',
          dataType: SyncDataType.Text,
          value: details.delayCode,
        },
        {
          field: 'DelayComment',
          dataType: SyncDataType.Text,
          value: details.delayComment,
        },
        {
          field: 'CloseRo',
          dataType: SyncDataType.Text,
          value: details.closeRo ? 'Y' : 'N',
        },
        {
          field: 'LabourType',
          dataType: SyncDataType.Text,
          value: details.labourType,
        },
        {
          field: 'WorkCategory',
          dataType: SyncDataType.Text,
          value: details.workCategory,
        },
        {
          field: 'IsDiagnostic',
          dataType: SyncDataType.Text,
          value: details.isDiagnostic ? 'Y' : 'N',
        },
        {
          field: 'TechDistance',
          dataType: SyncDataType.Number,
          value: (details.distanceAccurate || 0).toString(),
        },
      ];
    }

    // Location
    if (position?.latitude && position?.longitude) {
      body.syncData = [
        ...body.syncData,
        {
          field: 'Latitude',
          dataType: SyncDataType.Text,
          value: position.latitude.toString(),
        },
        {
          field: 'Longitude',
          dataType: SyncDataType.Text,
          value: position?.longitude?.toString(),
        },
        {
          field: 'Accuracy',
          dataType: SyncDataType.Text,
          value: position?.accuracy?.toString() || '0',
        },
      ];
    }

    const response = await axiosDmsInstance.post(`${url}`, body, {
      params,
    });

    return response.data;
  } catch (err: any) {
    return handleApiError('clockOffLabour', err);
  }
};
